import { Ionicons } from '@expo/vector-icons';
import React, { useState, useRef, useEffect } from 'react';
import {
	TextInput as RNTextInput,
	TextInputProps as RNTextInputProps,
	View,
	StyleSheet,
	TouchableOpacity,
	TextStyle,
	Modal,
	Platform,
	KeyboardAvoidingView,
	ScrollView,
	type TextInputInstance,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Text } from './text';

import { useTheme } from '@/components/theme-provider';


export interface TextInputProps extends RNTextInputProps {
	label?: string;
	error?: string | boolean;
	helperText?: string;
	mode?: 'flat' | 'outlined';
	left?: React.ReactNode;
	right?: React.ReactNode;
	dense?: boolean;
	disabled?: boolean;
	autoFocus?: boolean;
	enableFullScreen?: boolean; // Enable full-screen text editing on iOS (auto-enabled for multiline on mobile)
}

const TextInput: React.FC<TextInputProps> = ({
	label,
	error,
	helperText,
	mode = 'outlined',
	left,
	right,
	disabled = false,
	autoFocus = false,
	enableFullScreen,
	style,
	...props
}) => {
	const [isFullScreenVisible, setIsFullScreenVisible] = useState(false);
	const inputRef = useRef<TextInputInstance>(null);
	
	// Auto-enable full-screen mode for multiline inputs on mobile (iOS/iPadOS)
	// This provides a much better editing experience on iPad with keyboard
	const isMobile = Platform.OS !== 'web';
	const shouldEnableFullScreen = enableFullScreen ?? (isMobile && props.multiline);

	// Show full-screen input on iOS when autoFocus is true
	useEffect(() => {
		if (shouldEnableFullScreen && Platform.OS === 'ios' && autoFocus) {
			setIsFullScreenVisible(true);
		}
	}, [shouldEnableFullScreen, autoFocus]);

	const handleTextChange = (text: string) => {
		// Call the original onChangeText if provided
		if (props.onChangeText) {
			props.onChangeText(text);
		}
	};

	const handleDone = () => {
		setIsFullScreenVisible(false);
		inputRef.current?.blur();
	};

	const inputStyle: TextStyle = {
		backgroundColor: '#ffffff',
		color: '#212121',
		fontSize: 16,
		paddingHorizontal: 16,
		paddingVertical: 16,
		borderRadius: 8,
		opacity: disabled ? 0.6 : 1,
		minHeight: 56,
		borderWidth: 1,
		borderColor: error ? '#d32f2f' : '#e0e0e0',
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
		width: '100%',
		...(mode === 'flat' && {
			borderWidth: 0,
			borderBottomWidth: 2,
			borderBottomColor: error ? '#d32f2f' : '#2196f3',
			borderRadius: 0,
			boxShadow: 'none',
		}),
	};

	return (
		<>
			<View style={styles.container}>
				{label && (
					<Text style={[styles.label, error && { color: '#d32f2f' }]}>
						{label}
					</Text>
				)}
				<View style={styles.inputContainer}>
					{left && <View style={styles.leftContainer}>{left}</View>}
					<RNTextInput
						ref={inputRef}
						style={[inputStyle, style]}
						placeholderTextColor="#757575"
						autoFocus={autoFocus}
						onChangeText={handleTextChange}
						scrollEnabled={props.multiline}
						onFocus={() => {
							if (
								shouldEnableFullScreen
								&& Platform.OS === 'ios'
							) {
								setIsFullScreenVisible(true);
							}
						}}
						{...props}
					/>
					{right && <View style={styles.rightContainer}>{right}</View>}
				</View>
				{(error || helperText) && (
					<Text style={[styles.helperText, error ? { color: '#d32f2f' } : { color: '#757575' }]}>
						{typeof error === 'string' ? error : helperText}
					</Text>
				)}
			</View>

			{/* Full-screen text editor modal for iOS/iPad */}
			{shouldEnableFullScreen && Platform.OS === 'ios' && (
				<Modal
					visible={isFullScreenVisible}
					animationType="slide"
					presentationStyle="fullScreen"
					onRequestClose={handleDone}
				>
					<SafeAreaView style={styles.fullScreenContainer}>
						<View style={styles.fullScreenHeader}>
							{label && (
								<Text style={styles.fullScreenLabel}>{label}</Text>
							)}
							<TouchableOpacity onPress={handleDone} style={styles.doneButton}>
								<Text style={styles.doneButtonText}>Done</Text>
							</TouchableOpacity>
						</View>
						<KeyboardAvoidingView 
							style={{ flex: 1 }} 
							behavior="padding"
							keyboardVerticalOffset={60}
						>
							<ScrollView 
								style={styles.fullScreenScrollView}
								contentContainerStyle={styles.fullScreenScrollContent}
								keyboardShouldPersistTaps="handled"
								keyboardDismissMode="interactive"
								automaticallyAdjustKeyboardInsets={true}
							>
								<RNTextInput
									ref={inputRef}
									style={styles.fullScreenInput}
									placeholderTextColor="#757575"
									multiline={true}
									textAlignVertical="top"
									autoFocus={true}
									scrollEnabled={true}
									onChangeText={handleTextChange}
									{...props}
								/>
								{/* Extra space at bottom to keep cursor visible above keyboard */}
								<View style={{ height: 120 }} />
							</ScrollView>
						</KeyboardAvoidingView>
					</SafeAreaView>
				</Modal>
			)}
		</>
	);
};

// TextInput sub-components
export const TextInputIcon: React.FC<{
	icon: string | (() => React.ReactNode);
	onPress?: () => void;
	/** Needed on any PRESSABLE icon — without it the control cannot be reached from a test. */
	testID?: string;
}> = ({ icon, onPress, testID }) => {
	const theme = useTheme();
	return (
		<TouchableOpacity onPress={onPress} style={styles.iconContainer} testID={testID}>
			{typeof icon === 'function' ? icon() : <Ionicons name={icon as React.ComponentProps<typeof Ionicons>['name']} size={20} color={theme.colors.onSurface} />}
		</TouchableOpacity>
	);
};

export const TextInputAffix: React.FC<{ text: string }> = ({ text }) => {
	const theme = useTheme();
	return (
		<Text variant="bodyMedium" style={{ color: theme.colors.onSurface }}>
			{text}
		</Text>
	);
};

// Export the main component
export { TextInput };

const styles = StyleSheet.create({
	container: {
		marginVertical: 12,
	},
	label: {
		marginBottom: 6,
		fontSize: 14,
		fontWeight: '600',
		color: '#424242',
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		minHeight: 56,
		position: 'relative',
	},
	leftContainer: {
		position: 'absolute',
		left: 16,
		zIndex: 1,
	},
	rightContainer: {
		position: 'absolute',
		right: 16,
		zIndex: 1,
	},
	helperText: {
		marginTop: 6,
		fontSize: 12,
		color: '#757575',
		fontStyle: 'italic',
	},
	iconContainer: {
		padding: 8,
	},
	// Full-screen modal styles
	fullScreenContainer: {
		marginTop: Platform.OS === 'ios' ? 32 : 0,
		paddingTop: 5,
		flex: 1,
		backgroundColor: '#ffffff',
	},
	fullScreenHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingHorizontal: 16,
		paddingVertical: 12,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
		backgroundColor: '#f8f9fa',
	},
	fullScreenLabel: {
		fontSize: 18,
		fontWeight: '600',
		color: '#212121',
		flex: 1,
	},
	doneButton: {
		paddingHorizontal: 16,
		paddingVertical: 8,
		backgroundColor: '#007AFF',
		borderRadius: 8,
	},
	doneButtonText: {
		color: '#ffffff',
		fontSize: 16,
		fontWeight: '600',
	},
	fullScreenScrollView: {
		flex: 1,
	},
	fullScreenScrollContent: {
		flexGrow: 1,
		padding: 16,
	},
	fullScreenInput: {
		flex: 1,
		fontSize: 18,
		lineHeight: 26,
		color: '#212121',
		textAlignVertical: 'top',
		padding: 0,
		margin: 0,
		minHeight: 300,
	},
});
